<template>
  <section class="invoice-add-wrapper">
    <!-- Alert: No item found -->
    <b-alert variant="danger" :show="invoiceData === undefined">
      <h4 class="alert-heading">Error fetching invoice data</h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link class="alert-link" :to="{ name: 'apps-invoice-list' }">
          Invoice List
        </b-link>
        for other invoices.
      </div>
    </b-alert>
    <b-row v-if="invoiceData" class="invoice-add">
      <b-col cols="12" xl="8" md="7">
        <validation-observer ref="milestoneInvoiceValidation">
          <b-form v-on:submit.prevent="milestoneInvoiceValidationForm">
            <b-card no-body class="invoice-preview-card">

              <b-card-body class="invoice-padding pb-0">
                <div v-if="showLogo" class="d-flex align-items-center justify-content-center">
                  <div class="logo-wrapper">
                    <template v-if="companyLogo">
                      <img :src="companyLogo" alt="Company Logo" height="160" class="mb-3" />
                    </template>
                  </div>
                </div>

                <b-row class="invoice-spacing">
                  <!-- To Section -->
                  <b-col cols="12" md="4">
                    <h1 class="mb-3">Invoice</h1>
                    <b>{{ milestoneInfo?.project?.data?.lead?.data?.company_name }}</b>
                    <div v-if="milestoneInfo?.project?.data?.lead?.data?.key_contact_person">
                      {{ milestoneInfo?.project?.data?.lead?.data?.key_contact_person }}
                    </div>
                    <div v-if="milestoneInfo?.project?.data?.lead?.data?.address">
                      {{ milestoneInfo?.project?.data?.lead?.data?.address }}
                    </div>
                    <div v-if="milestoneInfo?.project?.data?.lead?.data?.email">
                      {{ milestoneInfo?.project?.data?.lead?.data?.email }}
                    </div>
                    <div v-if="milestoneInfo?.project?.data?.lead?.data?.mobile">
                      {{ milestoneInfo?.project?.data?.lead?.data?.mobile }}
                    </div>
                  </b-col>

                  <!-- Date Section -->
                  <b-col cols="12" md="4" class="text-md-left">
                    <div class="mb-3">
                      <h6 class="invoice-title">Invoice No.</h6>
                      <b-input-group class="input-group-merge invoice-edit-input-group disabled">
                        <b-input-group-prepend is-text>
                          <feather-icon icon="HashIcon" />
                        </b-input-group-prepend>
                        <b-form-input id="invoice-data-id" v-model="invoiceId" disabled />
                      </b-input-group>
                    </div>

                    <div class="mb-3">
                      <h6 class="invoice-title">Invoice Date</h6>
                      <ValidationProvider name="issue date" v-slot="{ errors }" vid="issue_date" rules="required">
                        <flat-pickr v-model="issueDate" class="form-control invoice-edit-input" />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </div>

                    <div class="mb-3">
                      <h6 class="invoice-title">Due Date</h6>
                      <ValidationProvider name="due date" v-slot="{ errors }" vid="due_date" rules="required">
                        <flat-pickr v-model="dueDate" class="form-control invoice-edit-input" />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </div>
                  </b-col>

                  <!-- From Section -->
                  <b-col cols="12" md="4" class="text-center text-md-left">
                    <h6 class="mb-3">{{ companyName }}</h6>
                    <div v-html="this.officeAddress"></div>
                    <p class="card-text mb-0">Phone: {{ this.officeMobile }}</p>
                    <p class="card-text mb-0">Email: {{ this.officeEmail }}</p>
                  </b-col>
                </b-row>
              </b-card-body>

              <b-card-body class="invoice-padding form-item-section">
                <div
                  ref="form"
                  class="repeater-form"
                  :style="{ height: trHeight }"
                >
                  <b-row
                    v-for="(item, index) in invoiceData.items"
                    :key="index"
                    ref="row"
                    class="pb-2"
                  >
                    <b-col cols="12">
                      <div class="d-none d-lg-flex">
                        <b-row class="flex-grow-1 px-1">
                          <b-col cols="12" lg="5"> Description </b-col>
                          <b-col cols="12" lg="3"> Unit Price </b-col>
                          <b-col cols="12" lg="2"> Quantity/Duration </b-col>
                          <b-col cols="12" lg="2"> Total </b-col>
                        </b-row>
                        <div class="form-item-action-col" />
                      </div>

                      <div class="d-flex border rounded">
                        <b-row class="flex-grow-1 p-2">
                          <!-- Single Item Form Headers -->
                          <b-col cols="12" lg="5">
                            <b-form-group>
                              <ValidationProvider
                                name="item"
                                v-slot="{ errors }"
                                vid="item"
                                rules="required"
                              >
                                <b-form-input
                                  v-model="item.itemTitle"
                                  type="text"
                                />
                                <small class="text-danger">{{
                                  errors[0]
                                }}</small>
                              </ValidationProvider>
                            </b-form-group>
                          </b-col>
                          <b-col cols="12" lg="3">
                            <b-form-group>
                              <ValidationProvider
                                name="amount"
                                v-slot="{ errors }"
                                vid="amount"
                                rules="required|min_value:0"
                              >
                                <b-form-input
                                  v-model="item.cost"
                                  type="number"
                                />
                                <small class="text-danger">{{
                                  errors[0]
                                }}</small>
                              </ValidationProvider>
                            </b-form-group>
                          </b-col>
                          <b-col cols="12" lg="2">
                            <b-form-group>
                              <ValidationProvider
                                name="quantity"
                                v-slot="{ errors }"
                                vid="quantity"
                                rules="required|min_value:1"
                              >
                                <b-form-input
                                  v-model="item.qty"
                                  type="number"
                                />
                                <small class="text-danger">{{
                                  errors[0]
                                }}</small>
                              </ValidationProvider>
                            </b-form-group>
                          </b-col>
                          <b-col cols="12" lg="2">
                            <label class="d-inline d-lg-none">Price</label>
                            <p class="mb-1">
                              {{ item.cost * item.qty }} {{ currency }}
                            </p>
                          </b-col>
                          <b-col cols="12" lg="5">
                            <b-form-group>
                              <ValidationProvider
                                name="description"
                                v-slot="{ errors }"
                                vid="description"
                              >
                                <b-form-textarea
                                  v-model="item.description"
                                  class="mb-lg-0"
                                />
                                <small class="text-danger">{{
                                  errors[0]
                                }}</small>
                              </ValidationProvider>
                            </b-form-group>

                            <label class="d-inline d-lg-none"
                              >Description</label
                            >
                          </b-col>
                        </b-row>

                        <div
                          class="d-flex flex-column justify-content-between border-left py-50 px-25"
                        >
                          <feather-icon
                            size="16"
                            icon="XIcon"
                            class="cursor-pointer"
                            @click="
                              invoiceData.items.length > 1
                                ? removeItem(index)
                                : null
                            "
                          />
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </div>
                <div>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    size="sm"
                    variant="primary"
                    @click="addNewItemInItemForm"
                  >
                    Add Item
                  </b-button>
                </div>
              </b-card-body>

              <b-card-body class="invoice-padding pb-0">
                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                    class="mt-md-0 mt-3 d-flex align-items-center"
                    order="2"
                    order-md="1"
                  >
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                    class="mt-md-6 d-flex justify-content-end"
                    order="1"
                    order-md="2"
                  >
                    <div class="invoice-total-wrapper custom-width">
                      <div class="invoice-total-item">
                        <p class="invoice-total-title">Sub Total:</p>
                        <p class="invoice-total-amount">
                          {{ subTotalAmount }} {{ currency }}
                        </p>
                      </div>
                      <!-- <div class="invoice-total-item">
                        <p class="invoice-total-title">
                          <b-form-checkbox
                            v-model="invoiceCreated"
                            @change="handleCheckboxClick"
                          >
                            <b>Include Vat Tax</b>
                          </b-form-checkbox>
                        </p>
                      </div> -->
                      <div class="invoice-total-item">
                        <p class="invoice-total-title">Vat:</p>

                        <b-form-group>
                          <ValidationProvider
                            name="vat amount"
                            v-slot="{ errors }"
                            vid="vat_amount"
                            rules="required|min_value:0"
                          >
                            <b-input-group :append="currency">
                              <b-form-input v-model="vatAmount" type="number" />
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </ValidationProvider>
                        </b-form-group>
                      </div>
                      <div class="invoice-total-item">
                        <p class="invoice-total-title">Tax:</p>

                        <b-form-group>
                          <ValidationProvider
                            name="tax amount"
                            v-slot="{ errors }"
                            vid="tax_amount"
                            rules="required|min_value:0"
                          >
                            <b-input-group :append="currency">
                              <b-form-input v-model="taxAmount" type="number" />
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </ValidationProvider>
                        </b-form-group>
                      </div>

                      <hr class="my-50" />
                      <div class="invoice-total-item">
                        <p class="invoice-total-title">Grand Total:</p>
                        <p class="invoice-total-amount">
                          {{ grandTotalAmount }} {{ currency }}
                        </p>
                      </div>

                      <template v-if="isSubmitLoading">
                        <b-button
                            class="float-right"
                            variant="primary"
                            disabled
                        >
                          <b-spinner small />
                          Loading...
                        </b-button>
                      </template>
                      <template v-else>
                        <template v-if="!this.invoiceCreated">
                          <b-button
                              type="submit"
                              class="float-right mt-1"
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              size=""
                              variant="primary"
                          >
                            Create Invoice
                          </b-button>
                        </template>

                        <template v-if="this.invoiceCreated">
                          <b-button
                              type="submit"
                              class="float-right mt-1"
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              size=""
                              variant="primary"
                              :disabled="!editable"
                          >
                            Save
                          </b-button>

                          <b-button
                              type="button"
                              class="float-right mt-1 mr-1"
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              size=""
                              variant="primary"
                              @click="editable = !editable"
                          >
                            Edit
                          </b-button>


                        </template>
                      </template>


                    </div>
                  </b-col>
                </b-row>
              </b-card-body>

              <!-- Spacer -->
              <hr class="invoice-spacing" />

              <!-- Note -->
              <b-card-body class="invoice-padding pt-0">
                <span class="font-weight-bold">Note: </span>

                <div :class="{ fullscreen: isFullscreen }">
                  <quill-editor
                      v-model="remarks"
                      :options="editorOption"
                      :style="{
                        width: '98% !important'
                      }"
                      id="payment_details"
                      placeholder="Payment Details"
                      class="custom-quill-editor"
                  >
                    <div id="toolbar" slot="toolbar">
                      <!-- Add a bold button -->
                      <span class="ql-formats">
                        <button class="ql-bold">
                          Bold
                        </button>
                        <button class="ql-italic">
                          Italic
                        </button>
                        <button class="ql-underline">
                          Underline
                        </button>
                        <button class="ql-strike">
                          Strike
                        </button>
                        <button
                            class="ql-blockquote"
                        ></button>
                        <button
                            class="ql-list"
                            value="ordered"
                        ></button>
                        <button
                            class="ql-list"
                            value="bullet"
                        ></button>
                        <button
                            class="ql-script"
                            value="sub"
                        ></button>
                        <button
                            class="ql-script"
                            value="super"
                        ></button>
                        <button
                            class="ql-indent"
                            value="-1"
                        ></button>
                        <button
                            class="ql-indent"
                            value="+1"
                        ></button>
                        <button
                            class="ql-direction"
                            value="rtl"
                        ></button>
                        <button
                            class="ql-align"
                            value=""
                        ></button>
                        <button
                            class="ql-align"
                            value="center"
                        ></button>
                        <button
                            class="ql-align"
                            value="right"
                        ></button>
                        <button
                            class="ql-align"
                            value="justify"
                        ></button>
                        <select class="ql-color"></select>
                        <select
                            class="ql-background"
                        ></select>
                        <select class="ql-size">
                          <option value="small" />

                          <option selected />
                          <option value="large" />
                          <option value="huge" />
                        </select>

                        <select class="ql-font"></select>
                        <select class="ql-header">
                          <option value="1">
                            Heading 1
                          </option>
                          <option value="2">
                            Heading 2
                          </option>
                          <option value="3">
                            Heading 3
                          </option>
                          <option value="4">
                            Heading 4
                          </option>
                          <option value="5">
                            Heading 5
                          </option>
                          <option value="6">
                            Heading 6
                          </option>
                          <option selected>Normal</option>
                        </select>

                        <button class="ql-link"></button>
                        <button type="button" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'" class="fullscreen-btn" @click="toggleFullscreen">
                          🔳
                        </button>
                      </span>
                    </div>
                  </quill-editor>
                </div>


              </b-card-body>
            </b-card>
          </b-form>
        </validation-observer>
      </b-col>

      <!-- Right Col: Card -->
      <b-col cols="12" md="5" xl="4" class="invoice-actions">
        <!-- Action Buttons -->
        <template v-if="this.invoiceCreated">
          <b-card>
            <!-- <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              class="mb-75"
              block
              v-on:click="printInvoice"
            >
              Print
            </b-button> -->

            <!-- Button: Add Payment -->
            <b-button
              v-b-toggle.sidebar-invoice-add-payment
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="success"
              class="mb-75"
              block
            >
              Collect Payment
            </b-button>
          </b-card>
        </template>
        <template v-if="previousInvoices">
          <div style="max-height: 500px; overflow-y: auto">
            <b-card title="Milestone Invoices">
              <app-timeline>
                <template v-for="(item, index) in previousInvoices">
                  <!-- <template > -->
                  <template>
                    <app-timeline-item
                      :key="item?.id"
                      variant="success"
                      icon="FileTextIcon"
                    >
                      <div
                        class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
                      >
                        <h6>
                          <b-link
                            @click="onShowPage(item?.milestone_id, item?.id)"
                            >{{ item?.invoice_no }}</b-link
                          >
                          <b-badge
                            v-if="item?.status == 'paid'"
                            variant="light-success"
                            class="ml-1"
                          >
                            Paid</b-badge
                          >
                          <b-badge v-else variant="light-danger" class="ml-1">
                            Due</b-badge
                          >
                          <b-badge
                            v-if="index == 0"
                            variant="light-primary"
                            class="ml-1"
                          >
                            Last Created</b-badge
                          >
                        </h6>
                        <small class="text-muted">
                          {{ formatDateTime(item?.created_at) }}</small
                        >
                      </div>

                      <div
                        class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
                      >
                        <!-- 1st Col -->
                        <div class="mb-1 mb-sm-0">
                          <span class="text-muted mb-50 d-block"
                            >Sub Total</span
                          >

                          <span>
                            {{ item?.sub_total_amount }}
                          </span>
                        </div>
                        <!-- 2nd Col -->
                        <div class="mb-1 mb-sm-0">
                          <span class="text-muted mb-50 d-block"
                            >VAT Amount</span
                          >
                          <span>{{ item?.vat_amount }}</span>
                        </div>
                        <!-- 3rd Col -->
                        <div>
                          <span class="text-muted mb-50 d-block"
                            >TAX Amount</span
                          >
                          <span>{{ item?.tax_amount }}</span>
                        </div>
                        <div>
                          <span class="text-muted mb-50 d-block"
                            >Grand Total</span
                          >
                          <span>{{ item?.grand_total_amount }}</span>
                        </div>
                      </div>
                    </app-timeline-item>
                  </template>
                </template>
              </app-timeline>
            </b-card>
          </div>
        </template>

        <b-card>
          <b-row class="mt-3">
            <b-col cols="12" xl="12" class="mb-lg-1">
              <h5 class="mb-30">Milestone Info:</h5>

              <p class="card-text mb-25">
                Title: {{ milestoneInfo?.title }}
              </p>

              <p class="card-text mb-25">
                Total Amount: {{ currency }} {{ milestoneInfo?.total_amount }}

              </p>
              <p class="card-text mb-25">
                Total Vat Amount:
                {{ currency }} {{ milestoneInfo?.total_vat_amount }}
              </p>
              <p class="card-text mb-25">
                Total Tax Amount:
                {{ currency }} {{ milestoneInfo?.total_tax_amount }}
              </p>
              <p class="card-text mb-25">
                Grand Total Amount:
                {{ currency }} {{ milestoneInfo?.grand_amount }}
              </p>
            </b-col>
          </b-row>

        </b-card>
      </b-col>
    </b-row>

    <invoice-sidebar-send-invoice />
    <template v-if="this.invoiceCreated">
      <invoice-sidebar-add-payment
        :invoiceInfo="invoiceInfo"
        :milestoneInfo="milestoneInfo"
        v-on:loadInvoiceInfo="updateInvoiceInfo"
      />
    </template>
  </section>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import Logo from "@core/layouts/components/Logo.vue";
import { mapGetters } from "vuex";
import { INVOICE_PAYMENT_ACCESS } from "@/helpers/permissionsConstant";

import { ref, onUnmounted } from "@vue/composition-api";
import { heightTransition } from "@core/mixins/ui/transition";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import router from "@/router/admin/index";
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormTextarea,
  BFormCheckbox,
  BPopover,
  BAlert,
  BLink,
  VBToggle,
  BBadge, BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { quillEditor } from "vue-quill-editor";

//   import invoiceStoreModule from '../invoiceStoreModule'
import InvoiceSidebarSendInvoice from "./InvoiceSidebarSendInvoice.vue";
import InvoiceSidebarAddPayment from "./InvoiceSidebarAddPayment.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";
import {
  required,
  email,
  integer,
  between,
  min_value,
} from "vee-validate/dist/rules";
export default {
  name: "InvoiceCreateView",
  components: {
    BSpinner,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormTextarea,
    BFormCheckbox,
    BPopover,
    BAlert,
    BLink,
    flatPickr,
    vSelect,
    Logo,
    InvoiceSidebarSendInvoice,
    InvoiceSidebarAddPayment,
    ValidationProvider,
    ValidationObserver,
    AppTimeline,
    AppTimelineItem,
    BBadge,
    quillEditor,
  },
  data() {
    return {
      companyName: "",
      editable: false,

      editorOption: {
        modules: {
          toolbar: "#toolbar",
        },
      },

      showLogo: true,
      isFullscreen: false,

      itemFormBlankItem: {
        item: null,
        cost: 0,
        qty: 1,
        price: 0,
        description: "",
      },
      invoiceData: {
        items: [
          {
            itemTitle: "",
            cost: 0,
            qty: 1,
            price: 0,
            description: "",
          },
        ],
      },

      INVOICE_PAYMENT_ACCESS,

      invoiceDescription: [],
      previousInvoices: [],
      milestoneInfo: "",
      officeAddress: "",
      officeEmail: "",
      officeMobile: "",
      accountInfo: "",
      invoiceId: "",
      remarks: "",
      dueDate: "",
      issueDate: "",
      discountAmount: 0,
      vatAmount: 0,
      taxAmount: 0,
      vatFixedAmount: 0,
      taxFixedAmount: 0,
      currency: "",
      invoiceNo: "",
      invoicePrefix: "",
      invoiceCode: "",
      invoiceInfo: "",
      invoiceCreated: false,
      isSubmitLoading: false,
    };
  },
  directives: {
    Ripple,
    "b-toggle": VBToggle,
  },
  mixins: [heightTransition],

  // Reset Tr Height if data changes
  watch: {
    // eslint-disable-next-line func-names
    "invoiceData.items": function () {
      this.initTrHeight();
    },
  },
  mounted() {
    this.initTrHeight();
  },
  async created() {
    window.addEventListener("resize", this.initTrHeight);

    this.loadItems();
  },
  destroyed() {
    window.removeEventListener("resize", this.initTrHeight);
  },
  computed: {
    ...mapGetters({
      filters: "invoiceModule/getFilters",
      permissions: "userModule/getPermissions",
    }),
    companyLogo() {
      return this.$store.getters["authModule/companyLogo"];
    },
    subTotalAmount() {
      return this.invoiceData.items.reduce(
        (total, item) => total + item.cost * item.qty,
        0
      );
    },
    grandTotalAmount() {
      return this.subTotalAmount + +this.vatAmount + +this.taxAmount;
    },
  },

  methods: {
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    updateInvoiceInfo() {
      this.invoiceCreated = false;

      this.loadItems();
    },

    onShowPage(milestoneId, id) {
      // admin-invoice-edit-details
      this.$router.push({
        name: "admin-invoice-edit-details",
        params: { milestoneId, id },
      });
    },
    formatDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY, h:mm:ss a");
      }
      return "Not Set";
    },
    lazyLoad(fn, ms) {
      return new Promise((resolve) => setTimeout(() => resolve(fn(), ms)));
    },
    windowPrint() {
      window.print();
    },
    async printInvoice() {
      let base = document.body.innerHTML;
      let content = document.getElementById("content").innerHTML;

      document.body.innerHTML = content;

      await this.lazyLoad(this.windowPrint, 500);
      document.body.innerHTML = base;
    },

    addNewItemInItemForm() {
      this.$refs.form.style.overflow = "hidden";
      this.invoiceData.items.push(
        JSON.parse(JSON.stringify(this.itemFormBlankItem))
      );

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight);
        setTimeout(() => {
          this.$refs.form.style.overflow = null;
        }, 350);
      });
    },
    removeItem(index) {
      this.invoiceData.items.splice(index, 1);
      this.trTrimHeight(this.$refs.row[0].offsetHeight);
    },
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.form ? this.$refs.form.scrollHeight : 0);
      });
    },

    async getSetting(params) {
      return await this.$api.get("/api/settings", {
        params: {
          select: params.select,
        },
      });
    },
    async getInvoiceSettings() {
      return await this.$api.get("api/settings/invoice");
    },
    async getInvoices() {
      return await this.$api.get("api/invoices");
    },
    async getInvoiceAll() {
      return await this.$api.get("api/invoices/last");
    },
    async getMilestoneInfo() {
      const id = this.$route.params.id;
      return await this.$api.get(
        `api/milestones/${id}?include=account.country,project.lead,milestoneForwardFromHistories.forwardFrom,milestoneForwardFromHistories.forwardTo,milestoneForwardToHistories.forwardFrom,milestoneForwardToHistories.forwardTo,invoices`
      );
    },
    async getActiveAccountInfo() {
      return await this.$api.get("api/accounts/active/all?include=country");
    },
    async getCompany() {
      return await this.$api.get("/api/companies/current");
    },
    async loadItems() {
      try {
        this.invoiceDescription = [];
        const [
          milestoneInfo,
          getSetting,
          accounts,
          invoiceSettings,
          invoiceInfo,
          company,
          invoiceAll,
        ] = await Promise.all([
          this.getMilestoneInfo(),
          this.getSetting({
            select: "account_id",
          }),
          this.getActiveAccountInfo(),
          this.getInvoiceSettings(),
          this.getInvoices(),
          this.getCompany(),
          this.getInvoiceAll(),
        ]);

        const today = new Date();

        // Format the date as 'YYYY-MM-DD'
        const formattedDate = today.toLocaleDateString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        this.issueDate = formattedDate;

        this.milestoneInfo = milestoneInfo?.data?.data;

        this.previousInvoices = this.milestoneInfo?.invoices?.data;
        this.previousInvoices.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        this.invoiceCode = invoiceSettings?.data?.data?.invoice_code;
        this.invoicePrefix = invoiceSettings?.data?.data?.invoice_prefix;

        const invoiceNumber =
          this.invoiceCode + invoiceAll?.data?.data?.invoice_code + 1;

        this.invoiceId = this.invoicePrefix + invoiceNumber;

        this.vatFixedAmount = this.milestoneInfo?.total_vat_amount;
        this.taxFixedAmount = this.milestoneInfo?.total_tax_amount;
        this.vatAmount = this.milestoneInfo?.total_vat_amount;
        this.taxAmount = this.milestoneInfo?.total_tax_amount;
        this.dueDate = this.milestoneInfo?.payment_deadline;

        this.currency = this.milestoneInfo?.currency;

        this.officeAddress = company?.data?.data?.address;
        this.officeEmail = company?.data?.data?.email;
        this.companyName = company?.data?.data?.name;
        this.officeMobile = company?.data?.data?.contact_no;

        if (this.milestoneInfo?.account) {
          this.accountInfo = this.milestoneInfo?.account?.data;
        } else {
          const defaultAccId = getSetting?.data?.data?.account_id;
          this.accountInfo = (accounts?.data?.data || []).find((item) => {
            if (item?.id == defaultAccId) {
              return item;
            }
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    async updateInvoice(invoiceItems) {
      const invoiceId = this.invoiceInfo?.id;
      return await this.$api.put(`api/invoices/${invoiceId}`, {
        tax_amount: this.taxAmount,
        vat_amount: this.vatAmount,
        sub_total_amount: this.subTotalAmount,
        grand_total_amount: this.grandTotalAmount,
        remarks: this.remarks,
        due_date: this.dueDate,
        items: invoiceItems,
        issue_date: this.issueDate,
      });
    },

    async createInvoice(milestoneId, invoiceItems) {
      return await this.$api.post(`api/invoice/${milestoneId}`, {
        currency: this.currency,
        tax_amount: this.taxAmount,
        vat_amount: this.vatAmount,
        discount_amount: this.discountAmount,
        sub_total_amount: this.subTotalAmount,
        grand_total_amount: this.grandTotalAmount,
        remarks: this.remarks,
        due_date: this.dueDate,
        items: invoiceItems,
        issue_date: this.issueDate,
        invoice_prefix: this.invoicePrefix,
        invoice_code: this.invoiceCode,
        invoice_no: this.invoiceId,
      });
    },
    milestoneInvoiceValidationForm: async function () {
      this.$refs.milestoneInvoiceValidation.validate().then(async (success) => {
        if (success) {
          try {
            const milestoneId = this.$route.params.id;
            let invoiceItems = [];

            this.invoiceData?.items.forEach((item) => {
              let data = {};
              data.title = item?.itemTitle;
              data.amount = item?.cost;
              data.quantity = item?.qty;
              data.total_amount = item?.cost * item?.qty;
              data.description = item?.description;
              invoiceItems.push(data);
            });

            this.isSubmitLoading = true;

            if (this.invoiceCreated) {
              try {
                const [invoiceInfo] = await Promise.all([
                  this.updateInvoice(invoiceItems),
                ]);

                // this.loadItems();
                if (invoiceInfo?.data?.data) {
                  this.invoiceCreated = true;
                  this.editable = false;
                  const [milestoneInfo] = await Promise.all([
                    this.getMilestoneInfo(),
                  ]);
                  this.milestoneInfo = milestoneInfo?.data?.data;
                  this.invoiceInfo = invoiceInfo?.data?.data;
                }
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Success",
                    icon: "BellIcon",
                    variant: "success",
                    text: "Invoice Updated Successfully",
                  },
                });
              } catch (error) {
                if (error?.response?.data?.message) {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Error",
                      icon: "BellIcon",
                      variant: "danger",
                      text: error?.response?.data?.message,
                    },
                  });
                }

                if (error?.response?.data?.errors) {
                  this.$refs.milestoneInvoiceValidation.setErrors(
                    error?.response?.data?.errors
                  );
                }
              }
            } else {
              try {
                const [invoiceInfo] = await Promise.all([
                  this.createInvoice(milestoneId, invoiceItems),
                ]);

                if (invoiceInfo?.data?.data) {
                  this.invoiceCreated = true;
                  this.editable = false;
                  const [milestoneInfo] = await Promise.all([
                    this.getMilestoneInfo(),
                  ]);
                  this.milestoneInfo = milestoneInfo?.data?.data;
                  this.invoiceInfo = invoiceInfo?.data?.data;
                }

                // this.loadItems();

                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Success",
                    icon: "BellIcon",
                    variant: "success",
                    text: "Invoice Created Successfully",
                  },
                });
              } catch (error) {
                if (error?.response?.data?.message) {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Error",
                      icon: "BellIcon",
                      variant: "danger",
                      text: error?.response?.data?.message,
                    },
                  });
                }

                if (error?.response?.data?.errors) {
                  this.$refs.milestoneInvoiceValidation.setErrors(
                    error?.response?.data?.errors
                  );
                }
              }
            }

            this.isSubmitLoading = false
          } catch (error) {
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }

            if (error?.response?.data?.errors) {
              this.$refs.milestoneInvoiceValidation.setErrors(
                error?.response?.data?.errors
              );
            }
          }
        }
      });
    },
  },
};
</script>

  <style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

  <style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

@import "@core/scss/vue/libs/quill.scss";


.form-item-section {
  background-color: $product-details-bg;
}

.form-item-action-col {
  width: 27px;
}

.repeater-form {
  // overflow: hidden;
  transition: 0.35s height;
}

.v-select {
  &.item-selector-title,
  &.payment-selector {
    background-color: #fff;

    .dark-layout & {
      background-color: unset;
    }
  }
}

.dark-layout {
  .form-item-section {
    background-color: $theme-dark-body-bg;

    .row .border {
      background-color: $theme-dark-card-bg;
    }
  }
}

.custom-width {
  max-width: 25rem !important;
}


/* Fullscreen mode */
.fullscreen {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* Adjust editor and toolbar in fullscreen mode */
.fullscreen .ql-container {
  height: calc(100vh - 50px) !important;
}

.fullscreen .ql-toolbar {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1000;
  padding: 10px;
}

/* Fullscreen button styling */
.fullscreen-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 10px;
}
</style>
